import { useEffect } from 'react';

import { useHasPermissions } from '~domains/auth';
import { isReportGenerated } from '~domains/transactions/entities/payment/lib/is-report-generated';
import {
  useCheckReportStatusQuery,
  useDownloadReportQuery,
} from '~domains/transactions/entities/report';

import { ampli } from '~shared/analytics';
import DownloadIcon from '~shared/assets/icons/download.svg?react';

import { Button } from '@breezeventures/ui-kit/button';

import { useGenerateReportMutation } from '../../api/applications-queries';
import type { FindApplicationsRequestParams } from '../../model/find-applications-params';

export const ApplicationsExport = ({
  params,
  disabled,
  size = 'm',
}: {
  params?: FindApplicationsRequestParams;
  disabled?: boolean;
  size?: 's' | 'm';
}) => {
  const { checkPermissions } = useHasPermissions();
  const canExport = checkPermissions(['APPLICATION_EXPORT']);
  const generateReportMutation = useGenerateReportMutation();
  const checkStatusQuery = useCheckReportStatusQuery(
    generateReportMutation.data?.reportId
  );
  const downloadQuery = useDownloadReportQuery({
    reportData: checkStatusQuery.data,
    screen: 'applications',
  });

  useEffect(() => {
    if (downloadQuery.isSuccess) {
      generateReportMutation.reset();
    }
  }, [downloadQuery.isSuccess, generateReportMutation]);

  if (!canExport) {
    return null;
  }

  const handleExportClick = () => {
    if (!params) return;
    generateReportMutation.mutate(params);
    ampli.applicationsScreenTapExport();
  };

  const reportGenerationInProgress =
    generateReportMutation.isSuccess &&
    !isReportGenerated(checkStatusQuery.data);
  const downloadInProgress =
    isReportGenerated(checkStatusQuery.data) && downloadQuery.isLoading;

  const showLoader =
    generateReportMutation.isPending ||
    reportGenerationInProgress ||
    downloadInProgress;

  return (
    <Button
      type="button"
      size={size}
      variant="secondary"
      loading={showLoader}
      disabled={showLoader || disabled || !params}
      onClick={handleExportClick}
    >
      {!showLoader && (
        <Button.Icon>
          <DownloadIcon />
        </Button.Icon>
      )}
      Export to Excel
    </Button>
  );
};
